import { wafflesKeyColumnName, printNumberAsString } from "@app/shared/waffles";
import { CalculationMethodEnum } from "@app/templates/templateModels";
import { AdequacyDetail } from "../network-assessment/adequacy"; 
import { AdequacyDetailWaffleToppings } from "../network-assessment/adequacy/AdequacyDetails";
import { ZipClassAggregateResultDisplayModel, ZipCodeDisplayModel } from "../zip-projects/interfaces";

type PreviewWaffleToppings = {
  [wafflesKeyColumnName]: string;
} & Pick<AdequacyDetailWaffleToppings,
  "accessAvgDist" | "accessAvgDist2" | "accessAvgTime" | "accessAvgTime2" | "accessMet" | "accessMetDist" |
  "accessMetTime" | "accessWith" | "accessWithout" | "marketAccess" |
  "marketDistinct" | "marketPercentage" | "marketProviderCount" |
  "overallGapAge" | "overallMet" | "servicingDistinct" | "servicingMet" |
  "servicingProviderCount" | "servicingRequired">;

const simulationPreviewWaffleToppings = (
    preview: AdequacyDetail[], 
    adequacyData: AdequacyDetail[], 
    cmsCreditIsApplying: boolean
): PreviewWaffleToppings[] => {
    if (preview.length === 0 || adequacyData.length === 0) {
      return [];
    }
    const bothRows = adequacyData.concat(preview);
    /* eslint-disable sort-keys */
    return bothRows.map((row, index) => {
      const isTimeOnlyCalculationMethod = !!row.accessSummary && row.accessSummary.calculationMethod.toString() === (CalculationMethodEnum.Time.toLowerCase());
      const isDistanceOnlyCalculationMethod = !!row.accessSummary && row.accessSummary.calculationMethod.toString() === (CalculationMethodEnum.Distance.toLowerCase());
      return {
        [wafflesKeyColumnName]: index ? "with-target" : "without-target",
        accessWith: row.isExpandedSpecialty && row.suppressSpecialtyAccessResults ? null : (row.accessSummary && row.accessSummary.memberCount > 0 && row.accessSummary.memberAccessPercentage.toFixed(1)) || "---",
        accessWithout: row.isExpandedSpecialty && row.suppressSpecialtyAccessResults ? null : (row.accessSummary && row.accessSummary.memberCount > 0 && (100 - row.accessSummary.memberAccessPercentage).toFixed(1)) || "---",
        accessAvgDist: row.isExpandedSpecialty && row.suppressSpecialtyAccessResults
          ? null
          : (!isTimeOnlyCalculationMethod && row.accessSummary && row.accessSummary.memberCount > 0 && row.accessSummary.averageCoveringDistance && row.accessSummary.averageCoveringDistance.toFixed(1)) || "---",
        accessAvgDist2: "---",
        accessAvgTime: row.isExpandedSpecialty && row.suppressSpecialtyAccessResults
          ? null
          : (!isDistanceOnlyCalculationMethod && row.accessSummary && row.accessSummary.memberCount > 0 && row.accessSummary.averageCoveringTime && row.accessSummary.averageCoveringTime.toFixed(1)) || "---",
        accessAvgTime2: "---",
        accessMetDist: getAccessMetDist(row, isTimeOnlyCalculationMethod),
        accessMetTime: getAccessMetTime(row, isDistanceOnlyCalculationMethod),
        accessMet: getAccessMet(row, cmsCreditIsApplying),
        servicingRequired: row.isExpandedSpecialty ? null : (row.servicingSummary ? printNumberAsString(row.servicingSummary.minimumServicingProviders, "---") : "---"),
        servicingProviderCount: (row.servicingSummary ? printNumberAsString(row.servicingSummary.inRegionProviderCount, "---") : "---"),
        servicingDistinct: (row.servicingSummary ? printNumberAsString(row.servicingSummary.distinctServicingProviderCount, "---") : "---"),
        servicingMet: row.isExpandedSpecialty ? null : (row.servicingSummary && (row.servicingSummary.metCriteria ? "Y" : "N")) || "---",
        overallMet: row.isExpandedSpecialty ? null :
          row.accessSummary.memberCount === 0 ? "---" :
            cmsCreditIsApplying && row.applicationCreditMetCriteria ? "Y" :
              row.metCriteria ? "Y" : "N",
        overallGapAge: printNumberAsString(row.gapAgeDays, "---"),
        marketAccess: row.isExpandedSpecialty && row.hideSpecialtyMarketAnalysis ? null :
          row.accessSummary.memberCount === 0 ? "---" : (row.marketSummary?.memberAccessPercentage?.toFixed(1)) || "---",
        marketProviderCount: row.isExpandedSpecialty && row.hideSpecialtyMarketAnalysis ? null :
          (row.marketSummary ? printNumberAsString(row.marketSummary.inRegionProviderCount, "---") : "---"),
        marketDistinct: row.isExpandedSpecialty && row.hideSpecialtyMarketAnalysis ? null :
          (row.marketSummary ? printNumberAsString(row.marketSummary.distinctServicingProviderCount, "---") : "---"),
        marketPercentage: row.isExpandedSpecialty && row.hideSpecialtyMarketAnalysis ? null :
          (row.marketSummary?.marketPercentage?.toFixed(1)) || "---",
      }
    });
    /* eslint-enable */
};

const zipSimulationPreviewWaffleToppings = (
  preview: ZipCodeDisplayModel[] | ZipClassAggregateResultDisplayModel[],
  adequacyData: ZipCodeDisplayModel[] | ZipClassAggregateResultDisplayModel[],
  calculationMethod: CalculationMethodEnum
): PreviewWaffleToppings[] => {
  if (preview.length === 0 || adequacyData.length === 0) {
    return [];
  }

  if (isZipCode(adequacyData[0])) {
    const zipCodeRows = (adequacyData as ZipCodeDisplayModel[]).concat(preview as ZipCodeDisplayModel[]);
    /* eslint-disable sort-keys */
    return zipCodeRows.map((row, index) => {
      const isTimeOnlyCalculationMethod = calculationMethod.toString() === (CalculationMethodEnum.Time.toLowerCase());
      const isDistanceOnlyCalculationMethod = calculationMethod.toString() === (CalculationMethodEnum.Distance.toLowerCase());
      return {
        [wafflesKeyColumnName]: index ? "with-target" : "without-target",
        accessWith: row.membershipCount > 0 && row.withAccessPercent.toFixed(1) || "---",
        accessWithout: row.membershipCount > 0 && (100 - row.withAccessPercent).toFixed(1) || "---",
        accessAvgDist: !isTimeOnlyCalculationMethod && row.membershipCount > 0 && row.averageDistance && row.averageDistance.toFixed(1) || "---",
        accessAvgDist2: !isTimeOnlyCalculationMethod && row.membershipCount > 0 && row.averageDistance2 && row.averageDistance2.toFixed(1) || "---",
        accessAvgTime: !isDistanceOnlyCalculationMethod && row.membershipCount > 0 && row.averageTime && row.averageTime.toFixed(1) || "---",
        accessAvgTime2: !isDistanceOnlyCalculationMethod && row.membershipCount > 0 && row.averageTime2 && row.averageTime2.toFixed(1) || "---",
        accessMetDist: !isTimeOnlyCalculationMethod && row.membershipCount > 0 && (row.metDist ? "Y" : "N") || "---",
        accessMetTime: !isDistanceOnlyCalculationMethod && row.membershipCount > 0 && (row.metTime ? "Y" : "N") || "---",
        accessMet: "---",
        servicingRequired: "---",
        servicingProviderCount: "---",
        servicingDistinct: "---",
        servicingMet: "---",
        overallMet: row.membershipCount === 0 ? "---" : (row.metPassPercent ? "Y" : "N"),
        overallGapAge: "---",
        marketAccess: row.marketAccessPercent ? printNumberAsString(row.marketAccessPercent, "---") : "---",
        marketProviderCount: "---",
        marketDistinct: row.marketServicing ? printNumberAsString(row.marketServicing, "---") : "---",
        marketPercentage: row.marketPercent ? printNumberAsString(row.marketPercent, "---") : "---",
      }
    });
  }
  else {
    const zipClassRows = (adequacyData as ZipClassAggregateResultDisplayModel[]).concat(preview as ZipClassAggregateResultDisplayModel[]);
    /* eslint-disable sort-keys */
    return zipClassRows.map((row, index) => {
      const isTimeOnlyCalculationMethod = calculationMethod.toString() === (CalculationMethodEnum.Time.toLowerCase());
      const isDistanceOnlyCalculationMethod = calculationMethod.toString() === (CalculationMethodEnum.Distance.toLowerCase());
      return {
        [wafflesKeyColumnName]: index ? "with-target" : "without-target",
        accessWith: row.membershipCount > 0 && row.withAccessPercent.toFixed(1) || "---",
        accessWithout: row.membershipCount > 0 && (100 - row.withAccessPercent).toFixed(1) || "---",
        accessAvgDist: !isTimeOnlyCalculationMethod && row.membershipCount > 0 && row.averageDistance && row.averageDistance.toFixed(1) || "---",
        accessAvgDist2: !isTimeOnlyCalculationMethod && row.membershipCount > 0 && row.averageDistance2 && row.averageDistance2.toFixed(1) || "---",
        accessAvgTime: !isDistanceOnlyCalculationMethod && row.membershipCount > 0 && row.averageTime && row.averageTime.toFixed(1) || "---",
        accessAvgTime2: !isDistanceOnlyCalculationMethod && row.membershipCount > 0 && row.averageTime2 && row.averageTime2.toFixed(1) || "---",
        accessMetDist: !isTimeOnlyCalculationMethod && row.membershipCount > 0 && (row.metDist ? "Y" : "N") || "---",
        accessMetTime: !isDistanceOnlyCalculationMethod && row.membershipCount > 0 && (row.metTime ? "Y" : "N") || "---",
        accessMet: "---",
        servicingRequired: "---",
        servicingProviderCount: "---",
        servicingDistinct: "---",
        servicingMet: "---",
        overallMet: row.membershipCount === 0 ? "---" : (row.metOverall ? "Y" : "N"),
        overallGapAge: "---",
        marketAccess: row.marketAccessPercent ? printNumberAsString(row.marketAccessPercent, "---") : "---",
        marketProviderCount: "---",
        marketDistinct: row.marketServicing ? printNumberAsString(row.marketServicing, "---") : "---",
        marketPercentage: row.marketPercent ? printNumberAsString(row.marketPercent, "---") : "---",
      }
    });
  }
};

const getAccessMetDist = (row: AdequacyDetail, isTimeOnlyCalculationMethod: boolean): string => {
  if (row.isExpandedSpecialty)
    return null;

  if (row.accessSummary && !isTimeOnlyCalculationMethod && row.accessSummary.memberCount > 0) {
    if (row.accessSummary.memberAccessByDistancePercentage) {
      if (row.accessSummary.metByDistance) {
        return "Y";
      } else {
        return "N";
      }
    } else {
      return "---";
    }
  } else {
    return "---";
  }
};

const getAccessMetTime = (row: AdequacyDetail, isDistanceOnlyCalculationMethod: boolean): string => {
  if (row.isExpandedSpecialty)
    return null;

  if (row.accessSummary && !isDistanceOnlyCalculationMethod && row.accessSummary.memberCount > 0) {
    if (row.accessSummary.memberAccessByTimePercentage) {
      if (row.accessSummary.metByTime) {
        return "Y";
      } else {
        return "N";
      }
    } else {
      return "---";
    }
  } else {
    return "---";
  }
};

const getAccessMet = (row: AdequacyDetail, cmsCreditIsApplying: boolean): string => {
  if (row.isExpandedSpecialty)
    return null;

  if (row.accessSummary && row.accessSummary.memberCount > 0) {
    if (cmsCreditIsApplying && row.accessSummary.applicationCreditMetCriteria) {
      return "Y";
    } else if (row.accessSummary.metCriteria) {
      return "Y";
    } else {
      return "N";
    }
  } else {
    return "---";
  }
}

function isZipCode(model: ZipCodeDisplayModel | ZipClassAggregateResultDisplayModel): model is ZipCodeDisplayModel {
  return 'metPassPercent' in model;
}

export {
  PreviewWaffleToppings,
  simulationPreviewWaffleToppings,
  zipSimulationPreviewWaffleToppings
};